import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { t } from "../../../i18n";
import { api } from "../../../services/api/api";

type ReportTemplateOption = {
  value: string;
  label: string;
};

/**
 * GET /admin/report-templates sits behind the /admin/** role gate; the customer form holding this
 * field does not. Being able to open the form is therefore no guarantee of being able to load the
 * catalogue -- it was @SuperAdmin in core until recently, so admins and planners got a 403 and an
 * empty option list, and swallowing that in console.error made it indistinguishable from "there are
 * no templates".
 *
 * The endpoint is @Admin now, which covers planners too, but the failure stays explicitly tracked:
 * on failure the field states why and locks, rather than silently offering an empty dropdown whose
 * only reachable action would be to clear a template the user could not then restore.
 */
type LoadState = "loading" | "loaded" | "failed";

export default function ReportTemplateSelect({
  value,
  onChange,
  className = ""
}: {
  value?: { id: string; name?: string } | null;
  onChange: (template: { id: string; name: string } | null) => void;
  className?: string;
}) {
  const [templates, setTemplates] = useState<EasyTech.ReportTemplate[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");

  useEffect(() => {
    let cancelled = false;

    api.get("admin/report-templates")
      .then((ret) => {
        if (cancelled) return;
        setTemplates(ret as EasyTech.ReportTemplate[]);
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setTemplates([]);
        setLoadState("failed");
      });

    return () => { cancelled = true; };
  }, []);

  const options: ReportTemplateOption[] = useMemo(
    () => templates.map((template) => ({
      value: template.id as string,
      label: `${template.name}${template.isDefault ? ` (${t("settings:reports:templates:defaultSuffix", "default")})` : ""}`
    })),
    [templates]
  );

  // Falling back to the customer's own {id, name} keeps the current template visible even when the
  // catalogue could not be loaded -- the user can still see what is configured, just not change it.
  const selectedOption = value?.id
    ? options.find((option) => option.value === value.id) ?? {
      value: value.id,
      label: value.name ?? value.id
    }
    : null;

  const hasFailed = loadState === "failed";

  return (
    <div className="w-full mr-2">
      <Select
        options={options}
        value={selectedOption}
        isClearable
        isDisabled={hasFailed}
        isLoading={loadState === "loading"}
        menuPosition="fixed"
        onChange={(option) => {
          if (!option) {
            onChange(null);
            return;
          }
          const template = templates.find((item) => item.id === option.value);
          onChange({
            id: option.value,
            name: template?.name ?? option.label
          });
        }}
        className={`w-full ${className}`}
        classNamePrefix="react-select"
        placeholder={t("client:form:fields:reportTemplate:placeholder")}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: "rgb(14 165 233)"
          }
        })}
      />

      {hasFailed && (
        <p className="mt-1 text-xs text-orange-700">
          {t("client:form:fields:reportTemplate:loadError")}
        </p>
      )}
    </div>
  );
}
